/**
 * Die BEDIENUNG der Spaltenbreiten der Bestellliste: Ziehen am Griff
 * (`startOrderListColumnResize`, index.html:7545), Pfeiltasten (`resizeOrderListColumnByKey`, 7568)
 * und Zuruecksetzen (`resetOrderListWidths`, 7576).
 *
 * ================= Was hier NICHT steht, und warum =================
 *
 * Das RECHNEN steht seit Task 13 in `lib/orderList.ts`: `orderListColumnSize` (index.html:7509),
 * `orderListColumnWidth` (7513) und `orderListTableWidth` (7518) - dort mit `breiten` als Argument
 * statt des Globals `ORDER_LIST_WIDTHS`. Eine zweite Fassung hier waere eine Abweichung vom
 * Original, keine Portierung davon. GEMELDET: der Task-Brief verlangt diese Datei fuer eben jene
 * drei Funktionen (Bericht, B8).
 *
 * `applyOrderListWidths` (7526-7537) hat hier bewusst KEIN Gegenstueck. Der Bestand greift damit
 * NACH dem Zeichnen per `document.querySelector` in die Tabelle und setzt drei Dinge nach: die
 * Breite jedes `<col>`, das `aria-valuenow` jedes Griffs und die Gesamtbreite der Tabelle. Genau
 * diese drei leitet die React-Fassung aus ihren Props ab (`BoardSeite`, `Spaltenkopf`) - das erneute
 * Zeichnen IST `applyOrderListWidths`. Die Gleichwertigkeit ist mechanisch und steht hier
 * ausdruecklich, statt stillschweigend angenommen zu werden.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `startOrderListColumnResize` verlaesst sich auf `event.button !== 0`: nur die linke Maustaste
 *    zieht. Bei jedem anderen Knopf passiert NICHTS - auch kein `preventDefault`.
 * 2. `preventDefault` UND `stopPropagation`: ohne das zweite liefe der Klick bis zum `<th>` und
 *    loeste dort das Sortieren aus.
 * 3. Die Breite waehrend des Ziehens rechnet gegen den STARTWERT, nicht gegen den letzten Zwischen-
 *    stand: `startWidth + moveEvent.clientX - startX`. Damit wandert die Kante mit dem Zeiger, auch
 *    wenn die Klemmung zwischenzeitlich griff (ein Zeiger, der ueber `max` hinaus und wieder
 *    zurueck geht, bringt die Spalte wieder unter `max` - mit einem inkrementellen Modell nicht).
 * 4. Der Startwert ist die GEKLEMMTE Breite (`orderListColumnWidth`), nicht der Rohwert aus
 *    `localStorage`.
 * 5. Zwei Klassen sind Nebenwirkungen am DOM, die kein Zustand ist: `resizing` am Griff und
 *    `is-resizing` am `<body>` (Letzteres unterdrueckt laut Stylesheet die Textauswahl waehrend des
 *    Ziehens). Beide werden hier genauso gesetzt und genauso wieder entfernt.
 * 6. Drei Ereignisse beenden das Ziehen: `pointerup`, `pointercancel` - und `pointermove` NICHT.
 *    Alle drei Zuhoerer haengen am `document`, nicht am Griff: der Zeiger verlaesst den 4 px breiten
 *    Griff sofort.
 * 7. Geschrieben wird `localStorage` erst am ENDE des Ziehens (7561), nicht bei jeder Bewegung. Bei
 *    den Pfeiltasten dagegen nach JEDEM Tastendruck (7573).
 * 8. Der Tastenschritt ist 8 px, mit `Shift` 24 px (7571). Andere Tasten als `ArrowLeft`/
 *    `ArrowRight` laufen wirkungslos durch - ohne `preventDefault`, damit Tab weiterhin springt.
 * 9. `resetOrderListWidths` setzt die Tabelle auf `{}` - es loescht den `localStorage`-Eintrag
 *    NICHT, es schreibt ein leeres Objekt hinein.
 */
import { orderListColumnSize, orderListColumnWidth } from "../../lib/orderList.ts";
import type { OrderListWidths } from "../../lib/orderList.ts";
import { schreibeBreiten, type Breiten } from "../../state/arbeitsplatz.ts";

/** index.html:7571 - Schrittweite der Pfeiltasten, mit `Shift` das Dreifache. */
export const BREITEN_SCHRITT = 8;
export const BREITEN_SCHRITT_GROSS = 24;

/**
 * `setOrderListColumnWidth` (index.html:7539) in reiner Form: klemmt und liefert eine NEUE Tabelle.
 * Das Speichern und das Neuzeichnen bleiben beim Aufrufer - im Bestand sind das die beiden Zeilen
 * `ORDER_LIST_WIDTHS[key] = …` und `applyOrderListWidths()`.
 */
export function mitGesetzterBreite(breiten: Breiten, key: string, width: number): Breiten {
  const size = orderListColumnSize(key);
  return { ...breiten, [key]: Math.max(size.min, Math.min(size.max, Math.round(width))) };
}

/** `resetOrderListWidths` (index.html:7576) - siehe Eigenheit 9. */
export function zurueckgesetzteBreiten(): Breiten {
  return {};
}

/**
 * Was der Aufrufer der Bedienung mitgeben muss: die aktuellen Breiten und die beiden Wirkungen, die
 * im Bestand Globals bzw. `applyOrderListWidths` waren.
 */
export interface BreitenBedienung {
  /** `ORDER_LIST_WIDTHS` - die aktuell gueltige Tabelle. */
  breiten: OrderListWidths;
  /** Zustandsaenderung: `ORDER_LIST_WIDTHS[key] = …` + `applyOrderListWidths()`. */
  onBreiten: (breiten: Breiten) => void;
  /**
   * `writeLocalJson('hubOrderListWidths', …)`. Optional: ohne Angabe schreibt die Kapsel selbst.
   * Der Test kann so ohne `localStorage` pruefen, und der Merge kann das Schreiben an einen
   * gemeinsamen Ort ziehen.
   */
  onSpeichern?: (breiten: Breiten) => void;
}

function speichere(bedienung: BreitenBedienung, breiten: Breiten): void {
  if (bedienung.onSpeichern) bedienung.onSpeichern(breiten);
  else schreibeBreiten(breiten);
}

/**
 * `startOrderListColumnResize(event, key)` (index.html:7545). Der Rueckgabewert ist der Abraeumer,
 * den die Tests benutzen; der Bestand hat keinen, weil `onEnd` sich selbst abmeldet.
 */
export function starteBreitenZiehen(
  ereignis: React.PointerEvent<HTMLElement>,
  key: string,
  bedienung: BreitenBedienung,
): void {
  // Eigenheit 1: nur die linke Maustaste, und bei jeder anderen passiert gar nichts.
  if (ereignis.button !== 0) return;
  ereignis.preventDefault();
  ereignis.stopPropagation();
  const handle = ereignis.currentTarget;
  const startX = ereignis.clientX;
  // Eigenheit 4: der GEKLEMMTE Startwert.
  const startWidth = orderListColumnWidth(key, bedienung.breiten);
  handle.classList.add("resizing");
  document.body.classList.add("is-resizing");
  // Eigenheit 3: gegen den Startwert rechnen, nicht inkrementell. `zuletzt` haelt den Stand fuer
  // das Speichern am Ende - im Bestand steht dort das Global `ORDER_LIST_WIDTHS`.
  let zuletzt: Breiten = { ...(bedienung.breiten as Breiten) };
  const onMove = (moveEvent: PointerEvent): void => {
    zuletzt = mitGesetzterBreite(zuletzt, key, startWidth + moveEvent.clientX - startX);
    bedienung.onBreiten(zuletzt);
  };
  const onEnd = (): void => {
    handle.classList.remove("resizing");
    document.body.classList.remove("is-resizing");
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerup", onEnd);
    document.removeEventListener("pointercancel", onEnd);
    // Eigenheit 7: erst am Ende schreiben.
    speichere(bedienung, zuletzt);
  };
  document.addEventListener("pointermove", onMove);
  document.addEventListener("pointerup", onEnd);
  document.addEventListener("pointercancel", onEnd);
}

/** `resizeOrderListColumnByKey(event, key)` (index.html:7568). */
export function breitenTaste(
  ereignis: React.KeyboardEvent<HTMLElement>,
  key: string,
  bedienung: BreitenBedienung,
): void {
  // Eigenheit 8: alles ausser den beiden Pfeilen laeuft wirkungslos durch.
  if (!["ArrowLeft", "ArrowRight"].includes(ereignis.key)) return;
  ereignis.preventDefault();
  const step = ereignis.shiftKey ? BREITEN_SCHRITT_GROSS : BREITEN_SCHRITT;
  const naechste = mitGesetzterBreite(
    bedienung.breiten as Breiten,
    key,
    orderListColumnWidth(key, bedienung.breiten) + (ereignis.key === "ArrowRight" ? step : -step),
  );
  bedienung.onBreiten(naechste);
  // Eigenheit 7: bei den Pfeiltasten nach JEDEM Druck.
  speichere(bedienung, naechste);
}
